// Imports
import SpyderAxios from './spyderAxiosConfig'
import firebase from '../config/FirebaseConfig'

// ========== Spyder API Brand Requests ==========

const getByBrandId = async (brandId) => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.get(
    '/brands',
    {
      params: { brand_ids: [brandId] },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  return data?.data?.[0] || null
}

// Brands that don't exist are left out, so the result can be shorter than brandIds
const getByBrandIds = async (brandIds) => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.get(
    '/brands',
    {
      // limit is capped at 1000 by the API
      params: { brand_ids: brandIds, limit: Math.min(brandIds.length, 1000) },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  return data?.data || []
}

const getByAdvertiserId = async (advertiserId) => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.get(
    '/brands',
    {
      params: { advertiser_ids: [advertiserId] },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )
  return data?.data?.[0] || null
}

const getBrandRankedAds = async (brandId, pageId, category, pagination) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/rankings`,
    {
      params: {
        brand_id: brandId,
        advertiser_id: pageId,
        category,
        limit: pagination.limit,
        page: pagination.page
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getBrandRankingsScatterData = async (brandId, pageId, date, compareDaysAgo) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/rankings/top-overview`,
    {
      params: { 
        brand_id: brandId,
        advertiser_id: pageId,
        date, 
        compare_days_ago: compareDaysAgo 
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getBrandCreativeVelocity = async (brandId, params) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { startDate, endDate, pageId = null } = params

  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/velocity`,
    {
      params: { 
        brand_id: brandId,
        ...(pageId ? { advertiser_id: pageId } : {}),
        start_date: startDate,
        end_date: endDate
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data.data
}

const getBrandPartners = async (brandId, params = {}) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { limit = 100, page = 1, pageId = null } = params

  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/partners`,
    {
      params: { 
        brand_id: brandId, 
        ...(pageId ? { advertiser_id: pageId } : {}),
        limit, 
        page 
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getBrandLandingPages = async (brandId, params) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { limit = 30, page = 1, pageId = null } = params

  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/landing-pages`,
    {
      params: {
        brand_id: brandId,
        ...(pageId ? { advertiser_id: pageId } : {}),
        limit,
        page
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const getBrandMonthlyTraffic = async (brandId, mode, params) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/monthly-traffic`,
    {
      params: {
        brand_id: brandId,
        mode,
        ...params
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data.data
}

const getCreativeTests = async (brandId, params = {}) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { dates = null, page = 1, limit = 25 } = params

  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/creative-tests`,
    {
      params: {
        brand_id: brandId,
        ...(dates ? { dates } : {}),
        page,
        limit
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

const findSimilarBrands = async (brandId, params = {}) => {
  // Pull the current token off the user
  const { Aa: token } = firebase.auth().currentUser

  const { 
    // Seed from a specific page id rather than all domain socials
    pageId = null, 
    // Number of similar brands to return
    size = 10, 
    // Restrict to brands with an ad-library page, enrich each with its spyder brand and live ad counts, 
    // and order by live ads rather than by similarity.
    fbPagesOnly = true, 
    // (string) Steer the comparison toward an aspect of the seed brands. 
    // Replaces the plain centroid with an embedding generated from the seeds' text under this focus.
    focus = null,
    // (string) Free-form steer applied the same way as focus
    instruction = null
  } = params

  const { data } = await SpyderAxios.get(
    `/brands/${brandId}/similar`,
    {
      params: {
        brand_id: brandId,
        size,
        fb_pages_only: fbPagesOnly,
        ...(pageId ? { advertiser_id: pageId } : {}),
        ...(focus ? { focus } : {}),
        ...(instruction ? { instruction } : {})
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data.data
}

// Brands the user (or their team) has saved ads for across their boards
const getLibraryBrands = async (params = {}) => {
  const token = await firebase.auth().currentUser?.getIdToken()

  // limit and search length are both capped at 200 by the API
  const { limit = 50, page = 1, favorites = false, search = '' } = params

  const { data } = await SpyderAxios.get(
    '/library/brands',
    {
      params: {
        limit,
        page,
        ...(favorites ? { favorites: true } : {}),
        ...(search ? { search } : {})
      },
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  )

  return data
}

// Favorites are the user's own, even on a team. Library brands come back with `is_favorited`.
const favoriteLibraryBrand = async (brandId) => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.put(
    `/library/brands/${encodeURIComponent(brandId)}/favorite`,
    null,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return data
}

const unfavoriteLibraryBrand = async (brandId) => {
  const token = await firebase.auth().currentUser?.getIdToken()
  const { data } = await SpyderAxios.delete(
    `/library/brands/${encodeURIComponent(brandId)}/favorite`,
    { headers: { Authorization: `Bearer ${token}` } }
  )
  return data
}

export default {
  // New Brand Requests (Spyder API)
  getByBrandId,
  getByBrandIds,
  getByAdvertiserId,
  getBrandMonthlyTraffic,
  getBrandCreativeVelocity,
  getBrandPartners,
  getBrandLandingPages,
  getCreativeTests,
  getBrandRankingsScatterData,
  getBrandRankedAds,
  findSimilarBrands,
  getLibraryBrands,
  favoriteLibraryBrand,
  unfavoriteLibraryBrand
}
